import { useCallback, useEffect, useMemo, useState } from "react";
import { continents, countries } from "countries-list";

let countryNameCodeMap = {};
Object.keys(countries).forEach((key) => {
  countryNameCodeMap[key] = countries[key].name;
});

const useGroupSearch = (allGroups = [], onGroupSelectionChange) => {
  // Initialize with "All" selected by default
  const [selectedOptions, setSelectedOptions] = useState([
    { label: "All", value: "all" },
  ]);

  // Memoize group-related sets and arrays for performance
  const { formattedGroups, groupValueSet } = useMemo(() => {
    const formatted = allGroups.map((group) => ({
      label: group.groupName,
      value: group.id,
    }));
    return {
      formattedGroups: formatted,
      groupValueSet: new Set(formatted.map((g) => g.value)),
    };
  }, [allGroups]);

  const { supportersRegions, regionValueSet } = useMemo(() => {
    const regions = Array.from(
      new Set(allGroups.map((group) => group.supportersRegion))
    ).map((code) => ({
      label: continents[code],
      value: code,
    }));
    return {
      supportersRegions: regions,
      regionValueSet: new Set(regions.map((r) => r.value)),
    };
  }, [allGroups]);

  const { supportersCountries, countryValueSet } = useMemo(() => {
    const countries = Array.from(
      new Set(allGroups.map((group) => group.supportersCountry))
    ).map((code) => ({
      label: countryNameCodeMap[code],
      value: code,
    }));
    return {
      supportersCountries: countries,
      countryValueSet: new Set(countries.map((c) => c.value)),
    };
  }, [allGroups]);

  const searchOptions = useMemo(() => {
    return [
      {
        label: "General",
        options: [
          {
            label: "All",
            value: "all",
          },
          {
            label: "Official Supporters Groups",
            value: "official-supporters-groups",
          },
        ],
      },
      {
        label: "Groups",
        options: formattedGroups.filter(
          (option) => option.value && option.label
        ),
      },
      {
        label: "Regions",
        options: supportersRegions.filter(
          (option) => option.value && option.label
        ),
      },
      {
        label: "Countries",
        options: supportersCountries.filter(
          (option) => option.value && option.label
        ),
      },
    ].filter((group) => group.options.length > 0); // Remove empty groups
  }, [formattedGroups, supportersCountries, supportersRegions]);

  // Calculate group selection result based on options
  const calculateGroupSelection = useCallback(
    (options) => {
      // Check what types of selections we have
      const hasAll = options.some((opt) => opt.value === "all");
      const hasOfficial = options.some(
        (opt) => opt.value === "official-supporters-groups"
      );

      // Return special identifiers for All or Official
      if (hasAll) {
        return ["All"];
      }

      if (hasOfficial) {
        return ["Official Groups"];
      }

      // For custom selections, expand regions/countries to group IDs
      const groupIds = new Set();

      options.forEach((option) => {
        if (groupValueSet.has(option.value)) {
          // Direct group selection
          groupIds.add(option.value);
        } else if (regionValueSet.has(option.value)) {
          // Region selection - add all groups in that region
          allGroups
            .filter((group) => group.supportersRegion === option.value)
            .forEach((group) => groupIds.add(group.id));
        } else if (countryValueSet.has(option.value)) {
          // Country selection - add all groups in that country
          allGroups
            .filter((group) => group.supportersCountry === option.value)
            .forEach((group) => groupIds.add(group.id));
        }
      });

      return Array.from(groupIds);
    },
    [allGroups, groupValueSet, regionValueSet, countryValueSet]
  );

  // Handle selection changes with interaction rules
  const handleSelectedOptionsChange = useCallback(
    (options) => {
      // Categorize current selections
      const hasAll = options.some((opt) => opt.value === "all");
      const hasOfficial = options.some(
        (opt) => opt.value === "official-supporters-groups"
      );
      const hasOtherOptions = options.some(
        (opt) =>
          opt.value !== "all" && opt.value !== "official-supporters-groups"
      );

      const regionOptions = options.filter((opt) =>
        regionValueSet.has(opt.value)
      );
      const countryOptions = options.filter((opt) =>
        countryValueSet.has(opt.value)
      );
      const groupOptions = options.filter((opt) =>
        groupValueSet.has(opt.value)
      );

      let filteredOptions = options;

      // Rule 1: If "All" or "Official" is selected WITH other options, clear All/Official
      if ((hasAll || hasOfficial) && hasOtherOptions) {
        filteredOptions = options.filter(
          (opt) =>
            opt.value !== "all" && opt.value !== "official-supporters-groups"
        );
      }
      // Rule 2: "All" selected alone - keep it
      else if (hasAll && !hasOtherOptions) {
        filteredOptions = [{ label: "All", value: "all" }];
      }
      // Rule 3: "Official Groups" selected alone - keep it
      else if (hasOfficial && !hasOtherOptions) {
        filteredOptions = [
          {
            label: "Official Supporters Groups",
            value: "official-supporters-groups",
          },
        ];
      }
      // Rule 4-6: No cross-category stacking
      else {
        // Determine which category the user is selecting from
        const categoryCount = [
          regionOptions.length > 0,
          countryOptions.length > 0,
          groupOptions.length > 0,
        ].filter(Boolean).length;

        // If user is mixing categories, keep only the newest category
        if (categoryCount > 1) {
          // Find what was just added by comparing with previous selections
          const previousValues = new Set(
            selectedOptions.map((opt) => opt.value)
          );
          const newOption = options.find(
            (opt) => !previousValues.has(opt.value)
          );

          if (newOption) {
            // Determine category of the new option
            if (regionValueSet.has(newOption.value)) {
              // Keep only regions
              filteredOptions = regionOptions;
            } else if (countryValueSet.has(newOption.value)) {
              // Keep only countries
              filteredOptions = countryOptions;
            } else if (groupValueSet.has(newOption.value)) {
              // Keep only groups
              filteredOptions = groupOptions;
            }
          } else {
            // User removed something, keep the dominant category
            if (groupOptions.length > 0) {
              filteredOptions = groupOptions;
            } else if (regionOptions.length > 0) {
              filteredOptions = regionOptions;
            } else if (countryOptions.length > 0) {
              filteredOptions = countryOptions;
            }
          }
        }
      }

      // Rule: Cannot have empty selection (fallback to "All")
      if (filteredOptions.length === 0) {
        filteredOptions = [{ label: "All", value: "all" }];
      }

      setSelectedOptions(filteredOptions);

      // Calculate and notify parent component
      if (onGroupSelectionChange) {
        const result = calculateGroupSelection(filteredOptions);
        onGroupSelectionChange(result);
      }
    },
    [
      selectedOptions,
      calculateGroupSelection,
      onGroupSelectionChange,
      regionValueSet,
      countryValueSet,
      groupValueSet,
    ]
  );

  // Initialize with "All" on mount
  useEffect(() => {
    if (allGroups.length > 0 && onGroupSelectionChange) {
      onGroupSelectionChange(["All"]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allGroups.length]); // Only on initial mount when groups are loaded

  // Calculate display count
  const count = useMemo(() => {
    const selection = calculateGroupSelection(selectedOptions);
    if (selection[0] === "All") {
      return `${allGroups.length} group(s)`;
    }
    if (selection[0] === "Official Groups") {
      const officialCount = allGroups.filter(
        (g) => g.groupType === "Official Supporters Group"
      ).length;
      return `${officialCount} group(s)`;
    }
    return `${selection.length} group(s)`;
  }, [selectedOptions, calculateGroupSelection, allGroups]);

  return {
    searchOptions,
    selectedOptions,
    count,
    onSelectedOptionsChange: handleSelectedOptionsChange,
  };
};

export default useGroupSearch;
